import React, { useContext, useState } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { Formik } from 'formik';
import useSWR from 'swr';
import axios from 'axios';
import { PAYMENTS_URL } from '@ordermentum/ui-constants';
import { ProfileUser, Retailer, UserFlags } from '@ordermentum/ui-types';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import { Input } from '../../../atoms/Input';
import Checkbox from '../../Input/Checkbox';
import Button from '../../Button/Button';
import { directDebitFormSchema } from '../../../utilities/formSchemas';
import { Theme, ThemeContext } from '../../../utilities/theme';
import {
  FormContainer,
  InputWrapper,
  TwoColumnInputWrapper,
  StickyFooter,
  FooterContent,
  FooterButtonWrappers,
  RightContainer,
  Footer,
} from './Form.styles';
import PurchaserToggleBlock from './PurchaserToggleBlock';
import { DirectDebitParams } from '../../../redux/payment_methods';
import ContentRow from '../../Cards/ContentRow';
import { fetcher } from '../../../swr/fetchers';

type StateProps = {
  params: { retailerId: string };
  purchaserId?: string;
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => void;
  fetchPayments?: (retailerId: string, flags: UserFlags) => void;
  flags: UserFlags;
  onSuccess?: (paymentMethodId: string) => void;
  onClose?: () => void;
  isStickyFooter?: boolean;
  hasDefaultForAllToggle?: boolean;
  hideCancel?: boolean;
  profile: ProfileUser;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  headerTitleColor: ColorCodes;
  textTitleColor: ColorCodes;
  subTextColor: ColorCodes;
  dividerColor: ColorCodes;
  iconColor: ColorCodes;
  descriptionTitleColor: ColorCodes;
  linkTextColor: ColorCodes;
  activeBackgroundColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  return {
    backgroundColor: colors.SWAN,
    headerTitleColor: colors.SPACE,
    textTitleColor: colors.SPACE,
    subTextColor: colors.STEEL,
    dividerColor: colors.ASH,
    iconColor: colors.SWEET_ORANGE,
    descriptionTitleColor: colors.STORM,
    linkTextColor: colors.SWEET_ORANGE,
    activeBackgroundColor: colors.LIGHT_CREAM,
  };
};

export const AddDirectFormComponent = (pageProps: StateProps) => {
  const {
    params,
    purchaserId,
    addDirectPayment,
    fetchPayments,
    flags,
    onClose,
    onSuccess = () => {},
    isStickyFooter = false,
    hasDefaultForAllToggle = false,
    hideCancel = false,
    profile,
  } = pageProps;
  const themeContext = useContext(ThemeContext);
  const { retailerId } = params;
  const theme = getTheme(themeContext);
  const { data: retailer } = useSWR<Retailer>(
    `/v1/retailers/${retailerId}`,
    fetcher()
  );
  const [financialInstituteName, setFinancialInstituteName] =
    useState<string>('');

  const { fullName, email, phone } = profile;
  const dateToday = new Intl.DateTimeFormat('en-AU').format(new Date()); // Format of dd/mm/yyyy

  const onSubmitFunction = async (values, actions) => {
    try {
      const payload = {
        account: values.accountNumber,
        accountName: values.accountName,
        bsb: values.bsb,
        country: 'AUS',
        currency: 'AUD',
        defaultPayment: true,
        defaultAll: values.defaultAll,
        type: 'bank',
        terms: values.acknowledgement,
        userId: retailerId,
        purchaserId,
      };

      const res: any = await addDirectPayment(payload, retailerId);

      if (fetchPayments) {
        await fetchPayments(retailerId, flags);
      }

      actions.resetForm();
      onSuccess(res.value.data.id);
    } catch (ex) {
      actions.setSubmitting(false);
    }
  };

  const handleBSBChange = async (value: string, setFieldValue) => {
    // BSB should be 6 digits
    if (value.length > 6) {
      return;
    }
    setFieldValue('bsb', value);

    if (value.length === 6) {
      try {
        const result = await axios.get<{ name: string }>(
          `${PAYMENTS_URL}/v1/validate/bsb?value=${value}`
        );

        if (result.data.name) {
          setFinancialInstituteName(result.data.name);
        }
      } catch {
        setFinancialInstituteName('');
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Formik
        validationSchema={directDebitFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={true}
        initialValues={{
          accountName: '',
          bsb: '',
          accountNumber: '',
          firstSignatory: '',
          secondSignatory: '',
          acknowledgement: false,
          defaultAll: false,
          currentlySubmitting: false,
        }}
        onSubmit={(values, actions) => {
          onSubmitFunction(values, actions);
        }}
      >
        {({
          dirty,
          handleSubmit,
          values,
          errors,
          setFieldValue,
          isSubmitting,
          resetForm,
        }) => (
          <form onSubmit={handleSubmit}>
            <RightContainer
              hasChanges={dirty}
              isStickyFooter={isStickyFooter}
              data-testid="payment-methods-add-direct"
            >
              <FormContainer>
                <InputWrapper>
                  <Input
                    tag={'account-name'}
                    inputLabel="Account name"
                    theme={themeContext}
                    hasError={!!errors.accountName}
                    errorLabel={errors.accountName}
                    size="max"
                    value={values.accountName}
                    onChange={(e, val) => {
                      setFieldValue('accountName', val);
                    }}
                    onBlur={(e, val) => {
                      setFieldValue('accountName', val);
                    }}
                  />
                </InputWrapper>
                <TwoColumnInputWrapper>
                  <InputWrapper>
                    <Input
                      tag="bsb"
                      inputLabel="BSB"
                      type="number"
                      theme={themeContext}
                      size="max"
                      value={values.bsb}
                      hasError={!!errors.bsb}
                      errorLabel={errors.bsb}
                      onChange={(_, value) =>
                        handleBSBChange(value, setFieldValue)
                      }
                    />
                    {financialInstituteName && (
                      <p className="text-body-small text-subtle">
                        {financialInstituteName}
                      </p>
                    )}
                  </InputWrapper>
                  <InputWrapper>
                    <Input
                      tag={'account-number'}
                      inputLabel="Account number"
                      theme={themeContext}
                      type="number"
                      size="max"
                      value={values.accountNumber}
                      hasError={!!errors.accountNumber}
                      errorLabel={errors.accountNumber}
                      onChange={(e, val) => {
                        if (val.length > 10) return;
                        setFieldValue('accountNumber', val);
                      }}
                      onBlur={(e, val) => {
                        if (val.length > 10) return;
                        setFieldValue('accountNumber', val);
                      }}
                    />
                  </InputWrapper>
                </TwoColumnInputWrapper>
                <InputWrapper>
                  <Input
                    tag={'first-signatory'}
                    inputLabel="First signatory"
                    theme={themeContext}
                    size="max"
                    value={values.firstSignatory}
                    hasError={!!errors.firstSignatory}
                    errorLabel={errors.firstSignatory}
                    onChange={(e, val) => {
                      setFieldValue('firstSignatory', val);
                    }}
                    onBlur={(e, val) => {
                      setFieldValue('firstSignatory', val);
                    }}
                  />
                </InputWrapper>
                <InputWrapper>
                  <Input
                    tag={'second-signatory'}
                    inputLabel="Second signatory (optional)"
                    theme={themeContext}
                    size="max"
                    value={values.secondSignatory}
                    onChange={(e, val) => {
                      setFieldValue('secondSignatory', val);
                    }}
                    onBlur={(e, val) => {
                      setFieldValue('secondSignatory', val);
                    }}
                  />
                </InputWrapper>

                <div className="flex w-full flex-row flex-wrap gap-10 border-y py-6">
                  <ContentRow label="Submitted by">
                    <p className="text-body text-regular">{fullName}</p>
                  </ContentRow>
                  <ContentRow label="Email">
                    <p className="text-body text-regular">{email}</p>
                  </ContentRow>
                  <ContentRow label="Phone">
                    <p className="text-body text-regular">
                      {phone ?? retailer?.phone ?? '--'}
                    </p>
                  </ContentRow>
                  <ContentRow label="Date">
                    <p className="text-body text-regular">{dateToday}</p>
                  </ContentRow>
                </div>

                {hasDefaultForAllToggle && (
                  <PurchaserToggleBlock
                    type="direct"
                    allowToggleAll
                    formikFunction={checked => {
                      setFieldValue('defaultAll', checked);
                    }}
                    formikValue={values.defaultAll}
                  />
                )}
                <Checkbox
                  name="acknowledgement"
                  checked={values.acknowledgement}
                  hasError={!!errors.acknowledgement}
                  errorLabel={errors.acknowledgement}
                  onChange={val => {
                    setFieldValue('acknowledgement', val);
                  }}
                >
                  <div className="flex flex-col">
                    <p className="text-body text-regular">
                      By checking this box, you accept the{' '}
                      <Button
                        as="a"
                        expandOnMobile={false}
                        variant="tertiary"
                        href="https://static.ordermentum.com/mobile/direct-debit-terms.html"
                      >
                        Ordermentum Direct Debit Agreement.
                      </Button>
                    </p>
                    <p className="text-body text-regular">
                      You also agree to this Direct Debit Request and the Direct
                      Debit Request service agreement, and authorise Zai
                      Australia Pty Ltd ABN 96 637 632 645 Direct Debit User ID
                      number 342203 and 481561 to debit your account through the
                      Bulk Electronic Clearing System (BECS) on behalf of
                      Ordermentum Pty Ltd (the “Merchant”) for any amounts
                      separately communicated to you by the Merchant. You
                      certify that you’re either an account holder or an
                      authorised signatory on the account listed above.
                    </p>
                  </div>
                </Checkbox>
              </FormContainer>
            </RightContainer>
            {isStickyFooter ? (
              <StickyFooter hasChanges={dirty}>
                <FooterContent>
                  <FooterButtonWrappers>
                    <Button
                      variant="secondary"
                      type="reset"
                      full
                      onClick={() => resetForm()}
                    >
                      Cancel
                    </Button>
                  </FooterButtonWrappers>
                  <FooterButtonWrappers>
                    <Button
                      variant="primary"
                      type="submit"
                      full
                      loading={isSubmitting}
                    >
                      Save payment method
                    </Button>
                  </FooterButtonWrappers>
                </FooterContent>
              </StickyFooter>
            ) : (
              <Footer>
                {!hideCancel && (
                  <Button
                    variant="tertiary"
                    type="reset"
                    onClick={() => {
                      resetForm();
                      if (onClose) {
                        onClose();
                      }
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  type="submit"
                  full={hideCancel}
                  loading={isSubmitting}
                >
                  Save
                </Button>
              </Footer>
            )}
          </form>
        )}
      </Formik>
    </ThemeProvider>
  );
};

export default AddDirectFormComponent;
